import React from "react";

import NarratorIntonation from "../intonation/NarratorIntonation";
import IntonationFeedbackDisplay from "../intonation/IntonationFeedbackDisplay";
import IntonationHelpModal from "../intonation/IntonationHelpModal";

const IntonationComparisonPanel = ({
  showIntonationVisualization,
  showIntonationHelp,
  setShowIntonationHelp,
  segment,
  assessment,
  subject,
  file,
  handleLaneWordClick,
  broadcastTime,
  audioref,
  visualizationView,
  rate,
  setRate,
  auth,
  resolvedAssessmentAudioUrl,
  resolvedNarratorAssessmentAudioUrl,
  fitViewport = false,
  compactViewport = false,
  userFirst = false,
  showRateMe = false,
  onRateMe,
}) => {
  if (!showIntonationVisualization) return null;

  const showUserFirst =
    Boolean(userFirst && assessment);

  return (
    <section
      className={[
        "w-full overflow-hidden rounded-2xl border backdrop-blur-sm transition-[border-color,box-shadow,background-color] duration-300",
        showUserFirst
          ? "border-purple-200/90 bg-white/95 shadow-[0_18px_50px_-30px_rgba(124,58,237,0.55)]"
          : "border-white/40 bg-white/80 shadow-lg shadow-blue-500/5",
        fitViewport
          ? "flex min-h-0 flex-1 flex-col"
          : "",
      ].join(" ")}
    >
      {showUserFirst && showRateMe && (
        <button
          type="button"
          onClick={onRateMe}
          className="group flex w-full shrink-0 items-center justify-between gap-4 border-b border-purple-700 bg-gradient-to-r from-purple-600 to-indigo-600 px-4 py-3 text-left text-white shadow-lg shadow-purple-500/20 transition-all hover:from-purple-700 hover:to-indigo-700 hover:shadow-xl hover:shadow-purple-500/25 active:scale-[0.998] sm:px-5"
        >
          <div className="flex min-w-0 items-center gap-3">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20 text-lg ring-1 ring-white/25"
              aria-hidden="true"
            >
              👥
            </span>

            <div className="min-w-0">
              <div className="text-sm font-bold sm:text-base">
                Get feedback on this recording
              </div>
              <div className="mt-0.5 text-xs text-white/80">
                Ask another person to listen and rate your pronunciation.
              </div>
            </div>
          </div>

          <span className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-bold text-purple-700 shadow-md transition-transform group-hover:translate-x-0.5">
            Rate me
            <span aria-hidden="true">→</span>
          </span>
        </button>
      )}

      <div
        className={[
          "shrink-0 border-b",
          showUserFirst
            ? "border-purple-100 bg-gradient-to-r from-purple-50/95 via-white to-blue-50/75"
            : "border-white/30 bg-gradient-to-r from-white/50 to-white/10",
          compactViewport
            ? "px-3 py-2"
            : "px-4 sm:px-5 py-3",
        ].join(" ")}
      >
        <div className="flex items-start gap-3">
          <div
            className={[
              "rounded-xl bg-white/80 border border-white/60 shadow-sm flex items-center justify-center shrink-0",
              compactViewport
                ? "w-8 h-8"
                : "w-9 h-9",
            ].join(" ")}
          >
            <span className={compactViewport ? "text-base" : "text-lg"}>
              📈
            </span>
          </div>

          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="truncate font-semibold text-gray-900">
                  {showUserFirst
                    ? "Review your intonation"
                    : "Follow the narrator’s intonation"}
                </h3>

                {showUserFirst && (
                  <p className="mt-0.5 text-xs text-slate-500">
                    Listen, inspect your curve, then request human feedback.
                  </p>
                )}
              </div>

              <span className="hidden sm:inline shrink-0 text-[11px] font-medium text-gray-500 bg-white/70 border border-white/60 px-2 py-0.5 rounded-full whitespace-nowrap">
                Press <span className="font-semibold">V</span> to change view
              </span>
            </div>

            <div
              className={[
                "flex flex-wrap items-center",
                compactViewport
                  ? "mt-1.5 gap-2"
                  : "mt-2 gap-3",
                showUserFirst
                  ? "rounded-xl border border-purple-100/80 bg-white/70 px-2.5 py-2"
                  : "",
              ].join(" ")}
            >
              <button
                type="button"
                onClick={() => setShowIntonationHelp(true)}
                className={[
                  "inline-flex items-center gap-2 rounded-xl bg-blue-50 text-blue-900 border border-blue-200 shadow-sm hover:bg-blue-100 transition text-xs font-semibold",
                  compactViewport
                    ? "px-2.5 py-1"
                    : "px-3 py-1.5",
                ].join(" ")}
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-lg bg-white border border-blue-200">
                  ⓘ
                </span>
                How to read the pitch curve
                <span className="text-[10px] font-bold text-blue-700 bg-white/70 border border-blue-200 px-1.5 py-0.5 rounded">
                  30 sec
                </span>
              </button>

              <span className="text-xs text-gray-600">
                <span className="font-semibold text-gray-800">Gaps</span>{" "}
                usually mean unvoiced sounds or brief pauses.
              </span>
            </div>
          </div>

          <span className="sm:hidden ml-auto text-[11px] font-medium text-gray-500 bg-white/70 border border-white/60 px-2 py-1 rounded-full">
            V = view
          </span>
        </div>
      </div>

      <div
        className={[
          "flex flex-col",
          fitViewport
            ? "min-h-0 flex-1"
            : "",
        ].join(" ")}
      >
        {segment?.words?.length > 0 && (
          <div
            className={[
              showUserFirst
                ? "order-2 border-t border-slate-100 bg-slate-50/35"
                : "order-1",
              fitViewport
                ? "flex min-h-0 flex-1 flex-col"
                : "",
              compactViewport
                ? "p-3"
                : "p-4 sm:p-5",
            ].join(" ")}
          >
            <div
              className={[
                "flex shrink-0 items-center gap-2",
                compactViewport ? "mb-2" : "mb-3",
              ].join(" ")}
            >
              <div className="w-7 h-7 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center text-white text-xs shadow-sm">
                🎭
              </div>

              <h4 className="font-semibold text-gray-800">Narrator</h4>

              <span className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200/60 px-2 py-0.5 rounded-full">
                Reference
              </span>
            </div>

            <div
              className={[
                "rounded-xl bg-white/60 border border-white/50 overflow-hidden",
                fitViewport
                  ? "flex min-h-[220px] flex-1 flex-col"
                  : "min-h-[220px] sm:min-h-[240px]",
              ].join(" ")}
            >
              <div
                className={[
                  fitViewport
                    ? "flex min-h-0 flex-1"
                    : "",
                  compactViewport
                    ? "p-1.5 sm:p-2"
                    : "p-2 sm:p-3",
                ].join(" ")}
              >
                <NarratorIntonation
                  subject={subject}
                  file={file}
                  segment={segment}
                  onWordClick={handleLaneWordClick}
                  broadcastTime={broadcastTime}
                  audioRef={audioref}
                  view={visualizationView}
                  speed={rate}
                  fitViewport={fitViewport}
                />
              </div>

              {!fitViewport && (
                <div className="px-3 pb-3 pt-2 border-t border-white/40">
                  <div className="rounded-xl bg-white/60 border border-white/50 backdrop-blur-sm px-4 py-3 shadow-sm w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="text-sm text-gray-700">
                        <span className="font-medium text-gray-900">Tip:</span>{" "}
                        Click a <span className="font-semibold">word</span> to replay it.
                        If it feels fast, try <span className="font-semibold">0.25×</span>.
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium text-gray-500">
                          Speed
                        </span>

                        <div className="inline-flex rounded-xl bg-white border border-gray-200 shadow-sm p-1">
                          {[0.25, 0.5, 1].map((speed) => {
                            const active = rate === speed;

                            return (
                              <button
                                key={speed}
                                onClick={() => {
                                  setRate(speed);
                                  if (audioref.current) {
                                    audioref.current.playbackRate = speed;
                                  }
                                }}
                                className={[
                                  "px-3 py-1.5 rounded-lg text-sm font-semibold transition-all",
                                  active
                                    ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow"
                                    : "text-gray-600 hover:bg-gray-50",
                                ].join(" ")}
                              >
                                {speed}x
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {assessment && (
          <div
            className={[
              showUserFirst
                ? "order-1 bg-gradient-to-b from-purple-50/35 to-white p-4 pt-3 sm:p-5 sm:pt-4"
                : "order-2 border-t border-white/25 p-4 sm:p-5",
            ].join(" ")}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center text-white text-xs shadow-sm">
                🎙️
              </div>

              <h4 className="font-semibold text-gray-800">Your voice</h4>

              <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-full">
                Your recording
              </span>

              <div className="ml-auto hidden md:inline text-[11px] text-gray-500">
                {showUserFirst
                  ? "Your latest result"
                  : "Compare your rise/fall pattern to the narrator’s"}
              </div>
            </div>

            <div
              className={[
                "min-h-[220px] overflow-hidden rounded-xl sm:min-h-[240px]",
                showUserFirst
                  ? "border border-purple-100 bg-white shadow-sm shadow-purple-500/5"
                  : "border border-white/50 bg-white/60",
              ].join(" ")}
            >
              <div className="p-2 sm:p-3">
                <IntonationFeedbackDisplay
                  assessment={assessment}
                  segment={segment}
                  view={visualizationView}
                  isAdmin={auth?.role === "ADMIN"}
                  audioUrl={resolvedAssessmentAudioUrl}
                  narratorAudioUrl={resolvedNarratorAssessmentAudioUrl}
                />
              </div>
            </div>
          </div>
        )}

        {!assessment && !fitViewport && (
          <div className="order-2 border-t border-white/25 p-4 sm:p-5">
            <div className="rounded-xl border border-dashed border-gray-300/70 bg-white/50 p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/70 border border-white/50 flex items-center justify-center">
                🎧
              </div>

              <div>
                <div className="font-semibold text-gray-800">
                  Record to see your curve here
                </div>

                <p className="text-sm text-gray-600">
                  After recording, you'll be able to replay your audio and compare
                  it with the narrator above.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <IntonationHelpModal
        open={showIntonationHelp}
        onClose={() => setShowIntonationHelp(false)}
      />
    </section>
  );
};

export default IntonationComparisonPanel;